import React from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Button,
  Container,
  Grid,
  GridItem,
  Heading,
  Icon,
  Input,
  Textarea,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  SimpleGrid,
  Avatar,
  Badge,
  useToast
} from '@chakra-ui/react';
import {
  FaWhatsapp,
  FaPhone,
  FaEnvelope,
  FaFacebookF,
  FaInstagram,
  FaTiktok,
  FaPaperPlane,
  FaClock,
  FaMapMarkerAlt,
  FaUsers
} from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const MotionBox = motion(Box);

const ContactPage = () => {
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const toast = useToast();

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm();

  const onSubmit = (data) => {
    console.log(data);
    toast({
      title: "تم إرسال الرسالة بنجاح",
      description: "سنتواصل معك في أقرب وقت ممكن.",
      status: "success",
      duration: 5000,
      isClosable: true,
      position: "top"
    });
    reset();
  };

  const contactInfo = [
    {
      icon: FaEnvelope,
      title: "البريد الإلكتروني",
      value: "loussifiacademy@gmail.com",
      link: "mailto:loussifiacademy@gmail.com"
    },
    {
      icon: FaPhone,
      title: "الهاتف",
      value: "22779137",
      link: "tel:+21622779137"
    },
    {
      icon: FaWhatsapp,
      title: "واتساب",
      value: "تواصل معنا مباشرة",
      link: "https://wa.me/21622779137"
    }
  ];

  return (
    <Box dir="rtl" bg="gray.50">
      <Header />

      {/* Hero Section */}
      <Box
        bgGradient={`linear(to-l, ${secondaryColor}, gray.800)`}
        color="white"
        py={{ base: 20, md: 32 }}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="-10%"
          right="-5%"
          width="400px"
          height="400px"
          bg={primaryColor}
          opacity="0.05"
          borderRadius="full"
          filter="blur(100px)"
        />

        <Container maxW="1400px" textAlign="center" position="relative" zIndex={1}>
          <VStack spacing={8}>
            <Badge
              bg={primaryColor}
              color={secondaryColor}
              px={6}
              py={2}
              borderRadius="full"
              fontSize="md"
              fontFamily="Tajawal, sans-serif"
              fontWeight="800"
            >
              اتصل بنا
            </Badge>
            <Heading fontSize={{ base: '4xl', md: '6xl' }} fontFamily="Tajawal, sans-serif" fontWeight="900" lineHeight="1.2">
              نحن هنا لمساعدة <br />
              <Text as="span" color={primaryColor}>التلاميذ والأولياء</Text>
            </Heading>
            <Text fontSize="xl" maxW="2xl" mx="auto" fontFamily="Tajawal, sans-serif" color="whiteAlpha.900" lineHeight="1.8">
              لديك سؤال؟ استفسار عن العروض؟ فريقنا متواجد دائماً لتقديم الدعم والإرشاد التربوي اللازم.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Main Section */}
      <Box py={{ base: 20, md: 32 }}>
        <Container maxW="1400px">
          <Grid templateColumns={{ base: '1fr', lg: '1.2fr 1fr' }} gap={16}>
            {/* Contact Form */}
            <Box>
              <VStack align="flex-start" spacing={10}>
                <Box>
                  <Heading size="xl" fontFamily="Tajawal, sans-serif" fontWeight="900" color={secondaryColor} mb={4}>
                    أرسل لنا رسالة
                  </Heading>
                  <Text fontSize="lg" fontFamily="Tajawal, sans-serif" color="gray.600">
                    املأ النموذج أدناه وسيقوم فريقنا بالتواصل معك في أقرب وقت.
                  </Text>
                </Box>

                <Box w="100%" bg="white" p={{ base: 8, md: 12 }} borderRadius="3xl" boxShadow="2xl" border="2px solid" borderColor="gray.100">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <VStack spacing={6}>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%">
                        <FormControl isInvalid={errors.name}>
                          <FormLabel fontFamily="Tajawal, sans-serif" fontWeight="700">الاسم الكامل</FormLabel>
                          <Input
                            placeholder="اسم التلميذ أو الولي"
                            size="lg"
                            h="60px"
                            bg="gray.50"
                            borderRadius="xl"
                            border="2px solid"
                            borderColor="transparent"
                            _focus={{ borderColor: primaryColor, bg: 'white' }}
                            {...register('name', { required: 'الرجاء إدخال الاسم' })}
                          />
                          <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={errors.email}>
                          <FormLabel fontFamily="Tajawal, sans-serif" fontWeight="700">البريد الإلكتروني</FormLabel>
                          <Input
                            placeholder="example@mail.com"
                            size="lg"
                            h="60px"
                            bg="gray.50"
                            borderRadius="xl"
                            border="2px solid"
                            borderColor="transparent"
                            _focus={{ borderColor: primaryColor, bg: 'white' }}
                            {...register('email', {
                              required: 'الرجاء إدخال البريد الإلكتروني',
                              pattern: { value: /^\S+@\S+$/i, message: 'بريد إلكتروني غير صالح' }
                            })}
                          />
                          <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                        </FormControl>
                      </SimpleGrid>

                      <FormControl isInvalid={errors.subject}>
                        <FormLabel fontFamily="Tajawal, sans-serif" fontWeight="700">الموضوع</FormLabel>
                        <Input
                          placeholder="استفسار عن عروض السنة التاسعة"
                          size="lg"
                          h="60px"
                          bg="gray.50"
                          borderRadius="xl"
                          border="2px solid"
                          borderColor="transparent"
                          _focus={{ borderColor: primaryColor, bg: 'white' }}
                          {...register('subject', { required: 'الرجاء إدخال الموضوع' })}
                        />
                        <FormErrorMessage>{errors.subject?.message}</FormErrorMessage>
                      </FormControl>

                      <FormControl isInvalid={errors.message}>
                        <FormLabel fontFamily="Tajawal, sans-serif" fontWeight="700">الرسالة</FormLabel>
                        <Textarea
                          placeholder="كيف يمكننا مساعدتك؟"
                          size="lg"
                          bg="gray.50"
                          borderRadius="xl"
                          border="2px solid"
                          borderColor="transparent"
                          _focus={{ borderColor: primaryColor, bg: 'white' }}
                          rows={6}
                          {...register('message', { required: 'الرجاء إدخال الرسالة' })}
                        />
                        <FormErrorMessage>{errors.message?.message}</FormErrorMessage>
                      </FormControl>

                      <Button
                        type="submit"
                        w="100%"
                        h="65px"
                        bg={primaryColor}
                        color={secondaryColor}
                        fontSize="xl"
                        fontWeight="900"
                        fontFamily="Tajawal, sans-serif"
                        _hover={{ bg: '#d4b016', transform: 'translateY(-3px)' }}
                        _active={{ transform: 'translateY(0)' }}
                        isLoading={isSubmitting}
                        leftIcon={<Icon as={FaPaperPlane} ml={2} />}
                        borderRadius="xl"
                        boxShadow="lg"
                        transition="all 0.3s"
                      >
                        إرسال الرسالة
                      </Button>
                    </VStack>
                  </form>
                </Box>
              </VStack>
            </Box>

            {/* Contact Info & Socials */}
            <VStack align="flex-start" spacing={10}>
              <Box>
                <Heading size="xl" fontFamily="Tajawal, sans-serif" fontWeight="900" color={secondaryColor} mb={4}>
                  معلومات التواصل
                </Heading>
                <Text fontSize="lg" fontFamily="Tajawal, sans-serif" color="gray.600">
                  تتوفر منصة كوبرا على عدة قنوات للتواصل لضمان سرعة الاستجابة.
                </Text>
              </Box>

              <VStack spacing={6} w="100%">
                {contactInfo.map((info, index) => (
                  <MotionBox
                    key={index}
                    whileHover={{ scale: 1.02 }}
                    w="100%"
                  >
                    <Box
                      as="a"
                      href={info.link}
                      target={info.icon === FaWhatsapp ? "_blank" : undefined}
                      bg="white"
                      p={8}
                      borderRadius="2xl"
                      boxShadow="xl"
                      display="flex"
                      alignItems="center"
                      border="1px solid"
                      borderColor="gray.100"
                    >
                      <Avatar
                        bg={`${primaryColor}15`}
                        icon={<Icon as={info.icon} color={primaryColor} boxSize={6} />}
                        mr={6}
                      />
                      <Box>
                        <Text fontSize="sm" fontFamily="Tajawal, sans-serif" fontWeight="800" color="gray.500">
                          {info.title}
                        </Text>
                        <Text fontSize="lg" fontFamily="Tajawal, sans-serif" fontWeight="900" color={secondaryColor}>
                          {info.value}
                        </Text>
                      </Box>
                    </Box>
                  </MotionBox>
                ))}

                <Box bg="white" p={8} borderRadius="2xl" boxShadow="xl" w="100%" border="1px solid" borderColor="gray.100">
                  <VStack align="flex-start" spacing={4}>
                    <HStack spacing={4}>
                      <Icon as={FaClock} color={primaryColor} boxSize={5} />
                      <Text fontFamily="Tajawal, sans-serif" fontWeight="800">أوقات العمل:</Text>
                    </HStack>
                    <VStack align="flex-start" spacing={1} pl={9}>
                      <Text fontFamily="Tajawal, sans-serif" color="gray.600">الاثنين - الجمعة: 8:00 - 18:00</Text>
                      <Text fontFamily="Tajawal, sans-serif" color="gray.600">السبت: 8:00 - 13:00</Text>
                    </VStack>
                  </VStack>
                </Box>

                <Box w="100%">
                  <Heading size="md" fontFamily="Tajawal, sans-serif" fontWeight="900" color={secondaryColor} mb={6}>
                    تابعنا على وسائل التواصل
                  </Heading>
                  <SimpleGrid columns={3} spacing={4}>
                    <Button
                      as="a"
                      href="https://www.facebook.com/profile.php?id=61564271291091"
                      target="_blank"
                      bg="#1877F2"
                      color="white"
                      _hover={{ opacity: 0.9, transform: 'translateY(-2px)' }}
                      leftIcon={<FaFacebookF />}
                      borderRadius="xl"
                      fontFamily="Tajawal, sans-serif"
                    >
                      فيسبوك
                    </Button>
                    <Button
                      as="a"
                      href="https://www.instagram.com/cobra.education/?hl=fr"
                      target="_blank"
                      bgGradient="linear(to-tr, #F58529, #DD2A7B, #8134AF)"
                      color="white"
                      _hover={{ opacity: 0.9, transform: 'translateY(-2px)' }}
                      leftIcon={<FaInstagram />}
                      borderRadius="xl"
                      fontFamily="Tajawal, sans-serif"
                    >
                      إنستغرام
                    </Button>
                    <Button
                      as="a"
                      href="https://www.tiktok.com/@cobraedu258"
                      target="_blank"
                      bg="black"
                      color="white"
                      _hover={{ opacity: 0.9, transform: 'translateY(-2px)' }}
                      leftIcon={<FaTiktok />}
                      borderRadius="xl"
                      fontFamily="Tajawal, sans-serif"
                    >
                      تيك توك
                    </Button>
                  </SimpleGrid>
                </Box>
              </VStack>
            </VStack>
          </Grid>
        </Container>
      </Box>

      {/* Map Section or Additional Info */}
      <Box py={{ base: 20, md: 32 }} bg="white">
        <Container maxW="1400px" textAlign="center">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={12}>
            <VStack spacing={4}>
              <Icon as={FaMapMarkerAlt} boxSize={10} color={primaryColor} />
              <Heading size="md" fontFamily="Tajawal, sans-serif" fontWeight="900">الموقع</Heading>
              <Text fontFamily="Tajawal, sans-serif" color="gray.600">تونس العاصمة، الجمهورية التونسية</Text>
            </VStack>
            <VStack spacing={4}>
              <Icon as={FaUsers} boxSize={10} color={primaryColor} />
              <Heading size="md" fontFamily="Tajawal, sans-serif" fontWeight="900">انضم إلينا</Heading>
              <Text fontFamily="Tajawal, sans-serif" color="gray.600">كن جزءاً من عائلة كوبرا التعليمية</Text>
            </VStack>
            <VStack spacing={4}>
              <Icon as={FaPaperPlane} boxSize={10} color={primaryColor} />
              <Heading size="md" fontFamily="Tajawal, sans-serif" fontWeight="900">الاستجابة</Heading>
              <Text fontFamily="Tajawal, sans-serif" color="gray.600">نرد على جميع الاستفسارات خلال 24 ساعة</Text>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default ContactPage;

